import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Briefcase, BarChart3, Settings, BadgeCheck,
  Star, Trash2, Edit, Eye, Ban, CheckCircle, Megaphone, Ticket
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";
import { PaymentSettingsCard } from "../components/PaymentSettingsCard";
import { PromoCodesAdmin } from "../components/PromoCodesAdmin";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      toast.error("Admin access required");
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, adsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/ads`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setAds(adsRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        updates,
        { withCredentials: true }
      );
      toast.success("User updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const decideAd = async (adId, action, reason = null) => {
    try {
      await axios.post(
        `${API}/admin/ads/${adId}/decision`,
        { action, reason },
        { withCredentials: true }
      );
      toast.success(`Banner ${action}d`);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, "Failed"));
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm("Delete this ad permanently?")) return;
    try {
      await axios.delete(`${API}/admin/ads/${adId}`, { withCredentials: true });
      toast.success("Ad deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete ad");
    }
  };

  const sendMonthlyReports = async () => {
    try {
      const r = await axios.post(`${API}/admin/ads/send-monthly-reports`, {}, { withCredentials: true });
      toast.success(`Reports sent: ${r.data.sent.length} / ${r.data.total}`);
    } catch (e) {
      toast.error(formatApiError(e, "Failed"));
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 md:px-8" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-title uppercase">
            ADMIN <span className="text-orange-500">DASHBOARD</span>
          </h1>
          <p className="text-zinc-500">Manage users, profiles, and platform settings</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-orange-600">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-orange-600">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="ads" className="data-[state=active]:bg-orange-600">
              <Megaphone className="h-4 w-4 mr-2" />
              Ads
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-orange-600" data-testid="admin-tab-promos">
              <Ticket className="h-4 w-4 mr-2" />
              Promo Codes
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <p className="font-heading text-3xl text-white">{stats?.users_count || 0}</p>
                <p className="text-zinc-500 text-sm">Total Users</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <p className="font-heading text-3xl text-white">{stats?.profiles_count || 0}</p>
                <p className="text-zinc-500 text-sm">Profiles</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <p className="font-heading text-3xl text-white">{stats?.jobs_count || 0}</p>
                <p className="text-zinc-500 text-sm">Total Jobs</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <p className="font-heading text-3xl text-orange-500">{stats?.premium_users || 0}</p>
                <p className="text-zinc-500 text-sm">Premium Users</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 p-6 text-center">
                <p className="font-heading text-3xl text-teal-500">{stats?.active_jobs || 0}</p>
                <p className="text-zinc-500 text-sm">Active Jobs</p>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-4 text-xs font-mono uppercase text-zinc-500">User</th>
                      <th className="text-left p-4 text-xs font-mono uppercase text-zinc-500">Type</th>
                      <th className="text-left p-4 text-xs font-mono uppercase text-zinc-500">Status</th>
                      <th className="text-left p-4 text-xs font-mono uppercase text-zinc-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-zinc-800 overflow-hidden">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-500">
                                  {u.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{u.name}</p>
                              <p className="text-zinc-500 text-sm">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="border-zinc-700 capitalize">
                            {u.user_type}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {u.is_verified && (
                              <span className="badge-verified">
                                <BadgeCheck className="h-3 w-3" />
                              </span>
                            )}
                            {u.is_premium && (
                              <span className="badge-premium">
                                <Star className="h-3 w-3" />
                              </span>
                            )}
                            {u.is_admin && (
                              <Badge className="bg-red-900/50 text-red-400 border border-red-700">
                                Admin
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateUser(u.user_id, { is_verified: !u.is_verified })}
                              className="text-zinc-400 hover:text-teal-400"
                            >
                              <BadgeCheck className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateUser(u.user_id, { is_premium: !u.is_premium })}
                              className="text-zinc-400 hover:text-orange-400"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Ads Tab */}
          <TabsContent value="ads" className="space-y-6">
            <PaymentSettingsCard />
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <h3 className="font-heading text-xl text-white">Banner Ads ({ads.length})</h3>
                <div className="flex gap-2">
                  <Button onClick={sendMonthlyReports} variant="outline" className="border-zinc-700" data-testid="send-monthly-reports-btn">
                    Send Monthly Reports
                  </Button>
                </div>
              </div>

              {ads.length > 0 ? (
                <div className="space-y-3" data-testid="admin-banners-list">
                  {ads.map((ad) => (
                    <div key={ad.ad_id} className="flex items-start gap-4 p-4 border border-zinc-800 bg-zinc-950" data-testid={`admin-banner-${ad.ad_id}`}>
                      <img
                        src={ad.image_url || ad.logo_url}
                        alt={ad.title}
                        className={`w-28 h-16 flex-shrink-0 ${ad.image_url ? "object-cover bg-zinc-900" : "object-contain p-3 bg-white"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium">{ad.title}</p>
                          <Badge className={`${ad.status === 'approved' ? 'bg-emerald-900/40 text-emerald-300' : ad.status === 'rejected' ? 'bg-red-900/40 text-red-300' : ad.status === 'pending' ? 'bg-yellow-900/40 text-yellow-300' : 'bg-zinc-800 text-zinc-400'} border-0 text-xs uppercase`}>
                            {ad.status}
                          </Badge>
                          <Badge className={`${ad.payment_status === "paid" ? "bg-emerald-900/40 text-emerald-300" : ad.payment_status === "partner" ? "bg-zinc-800 text-zinc-300" : "bg-red-900/40 text-red-300"} border-0 text-xs uppercase`} data-testid={`admin-banner-payment-${ad.ad_id}`}>
                            {ad.payment_status === "paid" ? `Paid R${ad.amount_paid ?? ad.price}` : ad.payment_status === "partner" ? "Partner" : "Unpaid"}
                          </Badge>
                          {!ad.is_active && ad.status === "approved" && (
                            <Badge className="bg-zinc-800 text-zinc-400 border-0 text-xs uppercase">Paused</Badge>
                          )}
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">
                          {ad.company_name || "Admin-created"} · {ad.placement} · {ad.start_date?.slice(0,10)} → {ad.end_date?.slice(0,10)}
                        </p>
                        {ad.advertiser_email && (
                          <p className="text-zinc-500 text-xs">{ad.advertiser_email}</p>
                        )}
                        <p className="text-zinc-400 text-xs mt-1">
                          {ad.impressions?.toLocaleString?.() || 0} impressions · {ad.clicks?.toLocaleString?.() || 0} clicks
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {ad.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => decideAd(ad.ad_id, "approve")} disabled={!["paid", "partner"].includes(ad.payment_status)} title={["paid", "partner"].includes(ad.payment_status) ? "" : "Awaiting payment"} className="bg-emerald-700 hover:bg-emerald-600" data-testid={`approve-${ad.ad_id}`}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              const reason = prompt("Rejection reason:");
                              if (reason) decideAd(ad.ad_id, "reject", reason);
                            }} className="border-zinc-700" data-testid={`reject-${ad.ad_id}`}>
                              Reject
                            </Button>
                          </>
                        )}
                        {ad.status === "approved" && (
                          <Button size="sm" variant="outline" onClick={() => decideAd(ad.ad_id, ad.is_active ? "pause" : "resume")} className="border-zinc-700">
                            {ad.is_active ? "Pause" : "Resume"}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteAd(ad.ad_id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-8">No banner ads yet. Advertisers can submit banners from their dashboard.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="promos" className="space-y-6">
            <PromoCodesAdmin />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
